import { useCallback } from 'react'
import { ScrollTrigger } from '../utils/gsap'
import {
  applyCharOpacityProgress,
  BOARD_SCRUB,
  BOARD_SLIDE_SWAP_THRESHOLD,
  BOARD_STORY_VISION_PIN_VH,
  computeVisionMissionScrollValues,
  getBoardPinStart,
  getPinScrollDistance,
} from '../utils/scrollAnimation'
import { useGsapScroll } from './useGsapScroll'

function applyPanelStyles(panel, y, opacity, otherOpacity) {
  if (!panel) return
  panel.style.transform = `translate3d(0, ${y}px, 0)`
  panel.style.opacity = String(opacity)
  panel.style.pointerEvents = opacity < 0.5 ? 'none' : 'auto'
  panel.style.zIndex = opacity >= otherOpacity ? '2' : '1'
}

export function useStoryVisionScroll(trackRef, pinRef, layerRefs, enabled) {
  const setup = useCallback(() => {
    const track = trackRef.current
    const pin = pinRef.current
    const layers = layerRefs.current
    if (!track || !pin) return

    ScrollTrigger.create({
      trigger: track,
      start: getBoardPinStart(),
      end: () => `+=${getPinScrollDistance(BOARD_STORY_VISION_PIN_VH)}`,
      pin,
      pinSpacing: true,
      scrub: BOARD_SCRUB,
      invalidateOnRefresh: true,
      anticipatePin: 1,
      preventOverlaps: true,
      onUpdate: (self) => {
        const values = computeVisionMissionScrollValues(self.progress)

        applyPanelStyles(layers.storyPanel, values.visionY, values.visionOpacity, values.missionOpacity)
        applyPanelStyles(layers.visionPanel, values.missionY, values.missionOpacity, values.visionOpacity)

        if (layers.storyLabel) {
          layers.storyLabel.style.opacity = String(values.visionOpacity)
        }
        if (layers.visionLabel) {
          layers.visionLabel.style.opacity = String(values.missionOpacity)
        }

        const storyTexts = [layers.storyText0, layers.storyText1].filter(Boolean)

        if (self.progress <= BOARD_SLIDE_SWAP_THRESHOLD) {
          const storyP = self.progress / BOARD_SLIDE_SWAP_THRESHOLD
          const segmentSize = storyTexts.length > 0 ? 1 / storyTexts.length : 1

          storyTexts.forEach((container, index) => {
            const start = index * segmentSize
            const end = (index + 1) * segmentSize

            if (storyP <= start) {
              applyCharOpacityProgress(container, 0)
            } else if (storyP >= end) {
              applyCharOpacityProgress(container, 1)
            } else {
              applyCharOpacityProgress(container, (storyP - start) / segmentSize)
            }
          })
        } else {
          storyTexts.forEach((container) => applyCharOpacityProgress(container, 1))
          const visionP =
            (self.progress - BOARD_SLIDE_SWAP_THRESHOLD) / (1 - BOARD_SLIDE_SWAP_THRESHOLD)
          applyCharOpacityProgress(layers.visionText, visionP)
        }
      },
    })
  }, [trackRef, pinRef, layerRefs])

  useGsapScroll(trackRef, enabled, setup)
}
